import React, { useEffect, useState } from "react";
import { Building2, PhoneCall, Menu, X, ChevronRight, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  useEffect(() => {
    document.title = "Privacy Policy – Metronavix";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Privacy Policy for Metronavix – Learn how we collect, use, and protect your information.");
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "/#about", label: "About" },
    { href: "/#services", label: "Services" },
    { href: "/#why", label: "Why Us" },
    { href: "/#process", label: "Process" },
    { href: "/#testimonials", label: "Proof" },
    { href: "/#contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">

      {/* ── NAVBAR (identical to landing) ─────────────────────────────────── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100" : "bg-white/80 backdrop-blur border-b border-transparent"
        }`}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg" aria-label="Metronavix – Home">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md">
                <Building2 className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              <span className="font-bold tracking-tight text-lg bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">
                Metronavix
              </span>
            </a>

            <nav className="hidden md:flex items-center gap-6 text-sm" role="navigation" aria-label="Main navigation">
              {navLinks.map(l => (
                <a key={l.href} href={l.href} className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                  {l.label}
                </a>
              ))}
              <div className="relative">
                <button
                  onClick={() => setLegalOpen(o => !o)}
                  className="flex items-center gap-1 text-blue-600 font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-haspopup="true"
                  aria-expanded={legalOpen}
                >
                  Legal <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${legalOpen ? "rotate-90" : ""}`} aria-hidden="true" />
                </button>
                {legalOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setLegalOpen(false)} />
                    <div className="absolute top-full right-0 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50">
                      <a href="/terms/privacy-policy" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-blue-700 bg-blue-50 font-semibold">
                        <span className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center text-xs">🔒</span>Privacy Policy
                      </a>
                      <a href="/terms" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors">
                        <span className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center text-xs">📋</span>Terms & Conditions
                      </a>
                    </div>
                  </>
                )}
              </div>
              <a href="/#contact" className="inline-flex items-center gap-2 rounded-xl px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <PhoneCall className="w-4 h-4" aria-hidden="true" /> Talk to us
              </a>
            </nav>

            <button className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              onClick={() => setMenuOpen(o => !o)} aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen}>
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map(l => (
                <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="py-3 px-4 text-gray-700 font-medium rounded-xl hover:bg-blue-50 hover:text-blue-700 transition-colors">{l.label}</a>
              ))}
              <div className="my-1 border-t border-gray-100 pt-2">
                <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Legal</p>
                <a href="/terms/privacy-policy" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 py-3 px-4 text-blue-700 font-semibold rounded-xl bg-blue-50">🔒 Privacy Policy</a>
                <a href="/terms" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 py-3 px-4 text-gray-700 font-medium rounded-xl hover:bg-purple-50 hover:text-purple-700 transition-colors">📋 Terms & Conditions</a>
              </div>
              <a href="/#contact" onClick={() => setMenuOpen(false)} className="mt-2 py-3 px-4 text-center rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-md">
                Book Free Consultation
              </a>
            </div>
          </div>
        )}
      </header>

      {/* ── PAGE HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50/60 to-pink-50" aria-hidden="true" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative py-14">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
            <a href="/" className="hover:text-blue-600 transition-colors">Home</a>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-700 font-medium">Privacy Policy</span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold mb-5">
            <Shield className="w-3 h-3" /> Legal Document
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
            Privacy{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Policy</span>
          </h1>
          <p className="mt-4 text-gray-500 text-lg">Last updated: 2025 · Metronavix Private Limited</p>
        </div>
      </section>

      {/* ── CONTENT ───────────────────────────────────────────────────────────── */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-gray-600 leading-relaxed space-y-5 text-[15px]">

          <p>This privacy policy ("Policy") explains the policy regarding the collection, use, disclosure and transfer of your information by Metronavix Private Limited and/or its subsidiary(ies) and/or affiliate(s) (collectively referred to as the "Company"), which operates various websites, platforms, and applications for delivery of information, products, offerings and content via any mobile or internet connected device (collectively the "Services").</p>
          <p>This Policy forms part and parcel of the Terms of Use. Please read this Privacy Policy and our Terms of Use carefully before using our Services. By using Metronavix website or its services, you consent to collection, storage, use, transfer, share and distribute the personal information you provide for any of the services that we offer.</p>

          <SectionHeading>1. Information Received, Collected And Stored by The Company</SectionHeading>
          <SubHeading>A. Information Supplied By Users</SubHeading>
          <h4 className="text-sm font-semibold text-gray-800 mt-3 mb-1">Registration data</h4>
          <p>When you register on the Sites for the Service, we ask you to provide basic contact information such as your name, sex, age, address, pin code, contact number, occupation, interests and email address. When you register using other accounts like Facebook, Twitter, Gmail etc. we shall retrieve Information from such accounts to continue providing the Services.</p>
          <h4 className="text-sm font-semibold text-gray-800 mt-3 mb-1">Subscription or paid service data</h4>
          <p>When you choose any subscription or paid service, we or our payment gateway provider may collect your purchase, address or billing information, including your credit card number and expiration date. The subscriptions or paid Services may be on auto renewal mode unless cancelled.</p>
          <h4 className="text-sm font-semibold text-gray-800 mt-3 mb-1">Voluntary information</h4>
          <p>We may collect additional information at other times, including but not limited to, when you provide feedback, comments, change your content or email preferences, respond to a survey, or any communications with us.</p>

          <SectionHeading>2. Information Automatically Collected / Tracked While Navigation</SectionHeading>
          <SubHeading>Cookies</SubHeading>
          <p>To improve the responsiveness of the Sites for our Users, we may use "cookies", or similar electronic tools to collect Information to assign each visitor a unique User Identification (User ID). The only personal information a cookie can contain is information you supply. A cookie cannot read data off your hard drive or device.</p>
          <h4 className="text-sm font-semibold text-gray-800 mt-3 mb-1">Opting out</h4>
          <p>If a User opts out using the Ads Settings, the unique DoubleClick cookie ID on the User's browser is overwritten with the phrase "OPT_OUT".</p>
          <SubHeading>Log File Information</SubHeading>
          <p>We automatically collect limited information about your computer's connection to the Internet, including your IP address, when you visit our site, application or service. We automatically receive and log information from your browser, including your IP address, your computer's name, your operating system, browser type and version.</p>
          <SubHeading>Clear GIFs</SubHeading>
          <p>We may use "clear GIFs" (Web Beacons) to track the online usage patterns of our Users in an anonymous manner. We may also use clear GIFs in HTML-based emails to track which emails are opened by recipients.</p>

          <SectionHeading>3. Information from Other Sources</SectionHeading>
          <p>We may receive information about you from other sources and treat it in accordance with this Policy. We use Google Analytics, among others, to track user behaviour on our Sites. You can opt-out of Google Analytics for display advertising using the Ads Settings options provided by Google.</p>

          <SectionHeading>Links to Third Party Sites / Ad-Servers</SectionHeading>
          <p>The Sites may include links to other websites governed by their respective privacy policies, which are beyond our control. We do not provide any personally identifiable information to third party websites / advertisers / ad-servers without your consent.</p>

          <SectionHeading>Information Used by The Company</SectionHeading>
          <p>The information supplied by the users enables us to improve the Services and provide you the most user-friendly experience. The Company may use user Information to maintain, protect, and improve the Services and for developing new services.</p>

          <SectionHeading>4. How Collected Data Is Used?</SectionHeading>
          <p>We use third-party advertising companies to serve ads when you visit or use our Sites or Services. Metronavix also allows advertisers and creators to serve advertisements directly.</p>

          <SectionHeading>5. Information Sharing</SectionHeading>
          <p>Personal information will be used to allow you to login to your account, resolve service issues, and communicate with you in relation to your use of the Site. The Company shares your Information with third parties only in limited circumstances: to conduct its business within its group companies; to enforce or protect rights; or when required by law.</p>
          <p>Your E-mail address is made available to other organisations whose products or services we think you might find interesting. Registered telephone numbers of customers may receive contact from us with information regarding new products, services or upcoming events.</p>

          <SectionHeading>6. Accessing and Updating Personal Information</SectionHeading>
          <p>When you use the Services or Sites, we make good faith efforts to provide you with access to your personal information and shall ensure that any personal information found to be inaccurate or deficient shall be corrected or amended as feasible.</p>

          <SectionHeading>7. Information Security</SectionHeading>
          <p>We take appropriate security measures to protect against unauthorized access to or unauthorized alteration, disclosure or destruction of data. These include internal reviews of our data collection, storage and processing practices and security measures, including appropriate encryption and physical security measures.</p>

          <SectionHeading>8. Updates / Changes</SectionHeading>
          <p>We may alter our Policy from time to time to incorporate necessary changes in technology, applicable law or any other variant. Any changes we make will be effective immediately on notice by posting the new policy on the Sites.</p>

          <SectionHeading>9. Miscellaneous</SectionHeading>
          <SubHeading>Security</SubHeading>
          <p>We use commercially reasonable security measures to protect the loss, misuse, and alteration of the information under our control. However, we cannot absolutely guarantee the protection of any information shared with us.</p>
          <SubHeading>Accuracy and Confidentiality of Account Information</SubHeading>
          <p>Customers are responsible for maintaining the secrecy and accuracy of password, email address, and other account information at all times.</p>
          <SubHeading>Third-Party Websites</SubHeading>
          <p>Our Site may contain links to third party websites. Navigating to another website does not make the Company liable for misuse of any information by any website controller to which we may link.</p>

          <SectionHeading>10. Questions / Grievance Redressal</SectionHeading>
          <p><strong className="text-gray-900">Redressal Mechanism:</strong> Any complaints, abuse or concerns with regards to the use, processing and disclosure of Information should immediately be informed to the designated Grievance Officers via email to{" "}
            <a href="mailto:Digital@Metronavix.com" className="text-blue-600 hover:underline font-medium">Digital@Metronavix.com</a>
          </p>
          <address className="not-italic rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-purple-50 p-6 mt-2">
            <strong className="text-gray-900">Metronavix Private Limited</strong><br />
            Plot No.787, Road No.36, 4th Floor,<br />
            Jubilee Hills, Shaikpet,<br />
            Hyderabad – 500033, Telangana, India
          </address>
          <p>This Privacy Policy is subject to changes. Please periodically review this page for the latest information on our privacy practices.</p>
        </div>

        {/* Cross-link card */}
        <div className="mt-14 p-6 rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-gray-900">Also read our Terms & Conditions</p>
            <p className="text-sm text-gray-500 mt-0.5">Understand the full terms of using Metronavix services.</p>
          </div>
          <a href="/terms" className="shrink-0 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg">
            Read Terms <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}

// ─── Shared Footer ────────────────────────────────────────────────────────────
function SharedFooter() {
  return (
    <footer className="py-14 border-t bg-gradient-to-br from-slate-900 to-gray-950 text-gray-300 mt-12" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-white text-lg">Metronavix</span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">PropTech for verified property discovery, quality inspections, and post‑handover support. Hyderabad → Pan‑India.</p>
          </div>
          <nav aria-label="Services navigation">
            <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Services</p>
            <ul className="space-y-2.5 text-sm">
              {["Property Sales Support", "Quality Inspections", "Builder QA/QC Support", "Interior Design & Modular", "Home Loan Support", "Advisory & Support"].map(s => (
                <li key={s}><a href="/#services" className="text-gray-400 hover:text-white transition-colors duration-200">{s}</a></li>
              ))}
            </ul>
          </nav>
          <nav aria-label="Company navigation">
            <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Company</p>
            <ul className="space-y-2.5 text-sm">
              <li><a href="/#about" className="text-gray-400 hover:text-white transition-colors duration-200">About</a></li>
              <li><a href="/#contact" className="text-gray-400 hover:text-white transition-colors duration-200">Contact</a></li>
              <li><a href="/terms/privacy-policy" className="text-gray-400 hover:text-white transition-colors duration-200">Privacy Policy</a></li>
              <li><a href="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">Terms & Conditions</a></li>
            </ul>
          </nav>
          <div>
            <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Get in touch</p>
            <address className="not-italic space-y-2 text-sm text-gray-400">
              <p><a href="mailto:founder@metronavix.com" className="hover:text-white transition-colors">founder@metronavix.com</a></p>
              <p>Jubilee Hills, Hyderabad</p>
              <p>Telangana 500033, India</p>
            </address>
            <a href="https://play.google.com/store/apps/details?id=in.metronavixhub.android" target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors">
              <span>📱</span> Google Play
            </a>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} Metronavix Private Limited. All rights reserved.</p>
          <p>Plot No.787, Road No.36, 4th Floor, Jubilee Hills, Hyderabad – 500033</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Typography helpers ───────────────────────────────────────────────────────
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-lg font-bold text-gray-900 mt-10 mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
      <span className="w-1 h-5 rounded-full bg-gradient-to-b from-blue-500 to-purple-500 inline-block" aria-hidden="true" />
      {children}
    </h2>
  );
}
function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-semibold text-gray-800 mt-5 mb-1.5">{children}</h3>;
}